import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import PlayCircleFilled from "@mui/icons-material/PlayCircleFilled";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTheme } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { ReactComponent as SwitchOnIcon } from "assets/images/icons/Toggle-On.svg";
import { ReactComponent as SwitchOffIcon } from "assets/images/icons/Toggle-Off.svg";
import { ReactComponent as CheckDefIcon } from "assets/images/icons/checkbox_default.svg";
import {
  DEFAULT_NVR_IMAGE_SETTINGS,
  DEFAULT_NVR_OSD_SETTINGS,
  DEFAULT_NVR_RECORDING_SETTINGS,
  NVR_ADVANCED_CHANNELS,
  NVR_ADVANCED_NAV,
  NVR_ADVANCED_SCENES,
} from "./const";

const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <Box
    onClick={() => {
      if (!disabled) onChange(!checked);
    }}
    sx={{
      display: "flex",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {checked ? <SwitchOnIcon /> : <SwitchOffIcon />}
  </Box>
);

const PrimaryCheckIcon = ({ color }) => (
  <Box
    component="svg"
    width={20}
    height={20}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="20" height="20" rx="5" fill={color} />
    <path
      d="M5.47363 10.6466L8.05965 13.2326L14.5247 6.76758"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const StreamTypeCheckbox = ({ label, checked, onChange }) => {
  const theme = useTheme();
  return (
    <FormControlLabel
      label={label}
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          icon={<CheckDefIcon style={{ width: 20, height: 20 }} />}
          checkedIcon={<PrimaryCheckIcon color={theme.palette.primary.main} />}
          sx={{
            padding: 0.5,
            "&:hover": { backgroundColor: "transparent" },
          }}
        />
      }
      sx={{
        margin: 0,
        gap: 1,
        ".MuiFormControlLabel-label": {
          fontSize: theme.typography.customFontSize[2],
          fontWeight: theme.typography.customFontWeight[1],
          userSelect: "none",
          lineHeight: 2.29,
        },
      }}
    />
  );
};

const ChannelSelect = ({ value, onChange }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 0.5 }}>
      <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Channel</Typography>
      <Select
        size="small"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        MenuProps={{ sx: { zIndex: 1400 } }}
        sx={{ minWidth: 88, height: 32, fontSize: 13 }}
      >
        {NVR_ADVANCED_CHANNELS.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const PreviewPane = () => (
  <Box
    sx={{
      width: 360,
      height: 180,
      borderRadius: 1,
      backgroundColor: "#8a8a8a",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <PlayCircleFilled sx={{ fontSize: 56, color: "rgba(255,255,255,0.9)" }} />
  </Box>
);

const ImageSliderRow = ({ label, value, onChange }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "110px 1fr 56px",
        alignItems: "center",
        gap: 2,
        py: 0.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>
        {label}
      </Typography>
      <Slider
        size="small"
        min={0}
        max={100}
        value={value}
        onChange={(_, next) => onChange(next)}
        sx={{ color: theme.palette.primary.main }}
      />
      <TextField
        size="small"
        value={value}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isNaN(next)) return;
          onChange(Math.min(100, Math.max(0, next)));
        }}
        inputProps={{ style: { textAlign: "center", padding: "4px 6px", fontSize: 13 } }}
        sx={{ width: 56 }}
      />
    </Box>
  );
};

const NvrAdvancedSettingsModal = ({ open, deviceName, onClose }) => {
  const theme = useTheme();
  const [navId, setNavId] = useState("display");
  const [displayTab, setDisplayTab] = useState("image");
  const [channel, setChannel] = useState("CH1");
  const [imageOpen, setImageOpen] = useState(true);
  const [imageSettings, setImageSettings] = useState(DEFAULT_NVR_IMAGE_SETTINGS);
  const [osdSettings, setOsdSettings] = useState(DEFAULT_NVR_OSD_SETTINGS);
  const [recordingSettings, setRecordingSettings] = useState(DEFAULT_NVR_RECORDING_SETTINGS);

  useEffect(() => {
    if (!open) return;
    setNavId("display");
    setDisplayTab("image");
    setChannel("CH1");
    setImageOpen(true);
    setImageSettings(DEFAULT_NVR_IMAGE_SETTINGS);
    setOsdSettings({ ...DEFAULT_NVR_OSD_SETTINGS, channelName: "CH1" });
    setRecordingSettings(DEFAULT_NVR_RECORDING_SETTINGS);
  }, [open]);

  useEffect(() => {
    setOsdSettings((prev) => (
      prev.channelName === channel || prev.showChannelName
        ? prev
        : { ...prev, channelName: channel }
    ));
  }, [channel]);

  const dirty = useMemo(() => {
    const defaultOsd = { ...DEFAULT_NVR_OSD_SETTINGS, channelName: channel };
    return JSON.stringify(imageSettings) !== JSON.stringify(DEFAULT_NVR_IMAGE_SETTINGS)
      || JSON.stringify(osdSettings) !== JSON.stringify(defaultOsd)
      || JSON.stringify(recordingSettings) !== JSON.stringify(DEFAULT_NVR_RECORDING_SETTINGS);
  }, [imageSettings, osdSettings, recordingSettings, channel]);

  const setImageValue = (key, value) => {
    setImageSettings((prev) => ({ ...prev, [key]: value }));
  };

  const setOsdValue = (key, value) => {
    setOsdSettings((prev) => ({ ...prev, [key]: value }));
  };

  const setRecordingValue = (key, value) => {
    setRecordingSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setImageSettings(DEFAULT_NVR_IMAGE_SETTINGS);
    setOsdSettings({ ...DEFAULT_NVR_OSD_SETTINGS, channelName: channel });
    setRecordingSettings(DEFAULT_NVR_RECORDING_SETTINGS);
    onClose();
  };

  return (
    <ModalContainer display={open} modalWidth="1250px">
      <Stack spacing={1.5} sx={{ minHeight: 640 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: 18, fontWeight: "bold", color: "#172664" }}>
            {`${deviceName || "<DeviceName>"} Advanced Settings`}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close advanced settings" sx={{ p: 0.5 }}>
            <CloseOutlined style={{ fontSize: 18, color: "rgba(0,0,0,0.45)" }} />
          </IconButton>
        </Stack>

        <Box sx={{ display: "flex", flex: 1, minHeight: 560, borderTop: `1px solid ${theme.palette.primary.greyThread}` }}>
          <Box
            sx={{
              width: 168,
              flexShrink: 0,
              borderRight: `1px solid ${theme.palette.primary.greyThread}`,
              py: 1.5,
            }}
          >
            {NVR_ADVANCED_NAV.map((item) => {
              const selected = item.id === navId;
              return (
                <Box
                  key={item.id}
                  onClick={() => setNavId(item.id)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: selected ? 700 : 500,
                    color: selected ? theme.palette.primary.main : theme.palette.primary.black,
                    borderRight: selected ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
                    "&:hover": { backgroundColor: "rgba(23,38,100,0.04)" },
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {navId === "display" ? (
              <>
                <Box sx={{ px: 2, pt: 1 }}>
                  <Tabs
                    value={displayTab}
                    onChange={(_, value) => setDisplayTab(value)}
                    sx={{
                      minHeight: 36,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        minHeight: 36,
                        fontWeight: 700,
                        color: theme.palette.primary.greyFifth,
                      },
                      "& .Mui-selected": { color: `${theme.palette.primary.main} !important` },
                    }}
                  >
                    <Tab label="Image" value="image" />
                    <Tab label="OSD" value="osd" />
                  </Tabs>
                </Box>

                {displayTab === "image" ? (
                  <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3, alignItems: "flex-start" }}>
                      <PreviewPane />
                      <ChannelSelect value={channel} onChange={setChannel} />
                    </Box>

                    <Box sx={{ borderTop: `1px solid ${theme.palette.primary.greyThread}`, pt: 1 }}>
                      <Box
                        onClick={() => setImageOpen((prev) => !prev)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          py: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.primary.black }}>
                          Image
                        </Typography>
                        {imageOpen
                          ? <KeyboardArrowUpIcon sx={{ color: theme.palette.primary.greyFifth }} />
                          : <KeyboardArrowDownIcon sx={{ color: theme.palette.primary.greyFifth }} />}
                      </Box>
                      {imageOpen && (
                        <Box sx={{ maxWidth: 520, pl: 1 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "110px 1fr",
                              alignItems: "center",
                              gap: 2,
                              py: 0.75,
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Scene</Typography>
                            <Select
                              size="small"
                              value={imageSettings.scene}
                              onChange={(event) => setImageValue("scene", event.target.value)}
                              MenuProps={{ sx: { zIndex: 1400 } }}
                              sx={{ width: 180, height: 32, fontSize: 13 }}
                            >
                              {NVR_ADVANCED_SCENES.map((option) => (
                                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                          <ImageSliderRow
                            label="Brightness"
                            value={imageSettings.brightness}
                            onChange={(value) => setImageValue("brightness", value)}
                          />
                          <ImageSliderRow
                            label="Saturation"
                            value={imageSettings.saturation}
                            onChange={(value) => setImageValue("saturation", value)}
                          />
                          <ImageSliderRow
                            label="Contrast"
                            value={imageSettings.contrast}
                            onChange={(value) => setImageValue("contrast", value)}
                          />
                          <ImageSliderRow
                            label="Sharpness"
                            value={imageSettings.sharpness}
                            onChange={(value) => setImageValue("sharpness", value)}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", gap: 2.5, flex: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3, alignItems: "flex-start" }}>
                      <PreviewPane />
                      <ChannelSelect value={channel} onChange={setChannel} />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "120px auto 1fr",
                        alignItems: "center",
                        columnGap: 2,
                        rowGap: 2,
                        maxWidth: 520,
                        pt: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Time</Typography>
                      <ToggleSwitch
                        checked={osdSettings.showTime}
                        onChange={(value) => setOsdValue("showTime", value)}
                      />
                      <Box />

                      <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Channel Name</Typography>
                      <ToggleSwitch
                        checked={osdSettings.showChannelName}
                        onChange={(value) => setOsdValue("showChannelName", value)}
                      />
                      <TextField
                        size="small"
                        value={osdSettings.channelName}
                        disabled={!osdSettings.showChannelName}
                        onChange={(event) => setOsdValue("channelName", event.target.value)}
                        inputProps={{ maxLength: 32, style: { fontSize: 13, padding: "6px 10px" } }}
                        sx={{ width: 180 }}
                      />
                    </Box>
                  </Box>
                )}
              </>
            ) : navId === "recording" ? (
              <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", flex: 1 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "160px 1fr",
                    alignItems: "center",
                    columnGap: 2,
                    rowGap: 2.5,
                    maxWidth: 560,
                    pt: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Channel</Typography>
                  <Select
                    size="small"
                    value={channel}
                    onChange={(event) => setChannel(event.target.value)}
                    MenuProps={{ sx: { zIndex: 1400 } }}
                    sx={{ width: 100, height: 32, fontSize: 13 }}
                  >
                    {NVR_ADVANCED_CHANNELS.map((option) => (
                      <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Enable Record</Typography>
                  <ToggleSwitch
                    checked={recordingSettings.enableRecord}
                    onChange={(value) => setRecordingValue("enableRecord", value)}
                  />

                  <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Enable Record Audio</Typography>
                  <ToggleSwitch
                    checked={recordingSettings.enableRecordAudio}
                    onChange={(value) => setRecordingValue("enableRecordAudio", value)}
                  />

                  <Typography sx={{ fontSize: 13, color: theme.palette.primary.black, alignSelf: "start", pt: 0.75 }}>
                    Stream Type
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <StreamTypeCheckbox
                      label="Main Stream"
                      checked={recordingSettings.mainStream}
                      onChange={(value) => setRecordingValue("mainStream", value)}
                    />
                    <StreamTypeCheckbox
                      label="Sub Stream"
                      checked={recordingSettings.subStream}
                      onChange={(value) => setRecordingValue("subStream", value)}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ fontSize: 13, color: theme.palette.primary.greyFifth }}>
                  {`${NVR_ADVANCED_NAV.find((item) => item.id === navId)?.label || ""} settings will be available later.`}
                </Typography>
              </Box>
            )}
            <Stack
              direction="row"
              justifyContent="center"
              spacing={1.5}
              sx={{ mt: "auto", pt: 6, pb: 1 }}
            >
              <OutlineButton width="90px" height="36px" onClick={handleCancel}>
                Cancel
              </OutlineButton>
              <SolidButton width="90px" height="36px" disabled={!dirty} onClick={onClose}>
                Apply
              </SolidButton>
            </Stack>
          </Box>
        </Box>
      </Stack>
    </ModalContainer>
  );
};

export default NvrAdvancedSettingsModal;
